import { SectionTitle, LuxuryTable, GoldBadge, MetricCard } from '@/components/Luxury';
import { formatCurrency, formatPercent } from '../../utils/formatters';

/**
 * Exit scenario data for a specific exit year.
 */
export interface ExitScenario {
  year: number;           // Exit year (3, 5, 7, or 10)
  propertyValue: number;  // Sale price at exit
  remainingLoan: number;  // Loan balance at exit
  equity: number;         // Net equity (property value - loan - sale costs)
  totalReturn: number;    // Total cash returned including cash flows
  irr: number;            // Internal rate of return as decimal (e.g., 0.25 for 25%)
}

export interface ExitAnalysisProps {
  exitScenarios: ExitScenario[];
  entryCapRate: number;   // Entry cap rate as decimal (e.g., 0.08 for 8%)
  exitCapRate: number;    // Exit cap rate as decimal (typically entry - 0.01)
  exchangeScenarios?: {
    year: number;
    netProceeds1031: number;
    deferredTaxes: number;
    qiFee: number;
  }[];
}

/**
 * ExitAnalysis component - Displays exit scenarios at different hold periods.
 *
 * Features:
 * - Cap rate compression/expansion display with dynamic labeling
 * - Exit scenarios table for years 3, 5, 7, 10
 * - Best exit year highlighted with Rec badge
 * - Best exit year callout card
 * - Uses LuxuryTable, SectionTitle, GoldBadge, MetricCard
 */
export function ExitAnalysis({
  exitScenarios,
  entryCapRate,
  exitCapRate,
  exchangeScenarios,
}: ExitAnalysisProps) {
  if (!exitScenarios || exitScenarios.length === 0) {
    return null;
  }

  // Find the best exit scenario (highest IRR)
  const bestScenario = exitScenarios.reduce((best, current) =>
    current.irr > best.irr ? current : best
  );

  // Sort scenarios by year
  const sortedScenarios = [...exitScenarios].sort((a, b) => a.year - b.year);

  // Calculate cap rate change in basis points (positive = compression, negative = expansion)
  const compressionBps = Math.round((entryCapRate - exitCapRate) * 10000);
  const isCompression = compressionBps >= 0;

  return (
    <div className="space-y-6">
      <SectionTitle>Exit Analysis by Year</SectionTitle>

      {/* Cap Rate Compression/Expansion Note */}
      <div className="flex items-center gap-4 p-4 bg-charcoal border border-slate-dark rounded-lg">
        <div className="text-sm text-stone-700">
          <span className="text-stone-700">
            Cap Rate {isCompression ? 'Compression' : 'Expansion'}:
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono tabular-nums text-lg text-ivory">
            {formatPercent(entryCapRate, 2)}
          </span>
          <span className="text-stone-700">-&gt;</span>
          <span className="font-mono tabular-nums text-lg text-gold">
            {formatPercent(exitCapRate, 2)}
          </span>
          <GoldBadge variant="off-market">
            {isCompression ? `-${compressionBps}bp` : `+${Math.abs(compressionBps)}bp`}
          </GoldBadge>
        </div>
      </div>

      {/* Exit Scenarios Table */}
      <div className="overflow-x-auto rounded-lg border border-slate-dark">
        <LuxuryTable>
          <thead>
            <tr>
              <th className="!text-center w-24">Exit Year</th>
              <th className="!text-right">Property Value</th>
              <th className="!text-right">Remaining Loan</th>
              <th className="!text-right">Net Equity</th>
              <th className="!text-right">Total Return</th>
              <th className="!text-right">IRR</th>
              {exchangeScenarios && (
                <th className="!text-right">1031 Proceeds</th>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedScenarios.map((scenario) => {
              const isRecommended = scenario.year === bestScenario.year;

              return (
                <tr
                  key={scenario.year}
                  className={
                    isRecommended
                      ? 'bg-amber-900/20 !font-semibold [&_td]:!text-amber-100'
                      : ''
                  }
                >
                  {/* Exit Year */}
                  <td className="!text-center">
                    <div className="flex items-center justify-center gap-2">
                      <span className="font-mono">Year {scenario.year}</span>
                      {isRecommended && (
                        <span className="px-2 py-0.5 text-sm uppercase tracking-wider bg-amber-600/30 text-amber-300 rounded">
                          Rec
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Property Value */}
                  <td className="num">
                    {formatCurrency(scenario.propertyValue)}
                  </td>

                  {/* Remaining Loan */}
                  <td className="num text-stone-700">
                    {formatCurrency(scenario.remainingLoan)}
                  </td>

                  {/* Net Equity */}
                  <td className="num">
                    {formatCurrency(scenario.equity)}
                  </td>

                  {/* Total Return */}
                  <td className="num text-emerald">
                    {formatCurrency(scenario.totalReturn)}
                  </td>

                  {/* IRR */}
                  <td className={`num font-semibold ${
                    isRecommended ? '!text-amber-100' : 'text-ivory'
                  }`}>
                    {formatPercent(scenario.irr, 1)}
                  </td>

                  {/* 1031 Proceeds (optional) */}
                  {exchangeScenarios && (() => {
                    const exchange = exchangeScenarios.find(e => e.year === scenario.year);
                    return (
                      <td className="num">
                        {exchange ? (
                          <div>
                            <div className="text-emerald">
                              {formatCurrency(exchange.netProceeds1031)}
                            </div>
                            <div className="text-sm text-emerald">
                              Deferred: {formatCurrency(exchange.deferredTaxes)}
                            </div>
                          </div>
                        ) : (
                          <span className="text-stone-700">--</span>
                        )}
                      </td>
                    );
                  })()}
                </tr>
              );
            })}
          </tbody>
        </LuxuryTable>
      </div>

      {/* Best Exit Year Callout */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard
          label="Recommended Exit"
          value={`Year ${bestScenario.year}`}
          hint={`Highest IRR at ${formatPercent(bestScenario.irr, 1)}`}
          variant="highlight"
        />
        <MetricCard
          label="Exit Property Value"
          value={formatCurrency(bestScenario.propertyValue)}
          hint={`At ${formatPercent(exitCapRate, 2)} exit cap`}
        />
        <MetricCard
          label="Total Return"
          value={formatCurrency(bestScenario.totalReturn)}
          hint="Including all cash flows"
          variant="green"
        />
      </div>

      {/* Footer note */}
      <p className="text-sm text-stone-700">
        Year {bestScenario.year} is the recommended exit based on highest IRR.
        Exit cap rate assumes {isCompression ? `${compressionBps}bp compression` : `${Math.abs(compressionBps)}bp expansion`} from entry cap.
      </p>
    </div>
  );
}
